import React, { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { api, useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { IdentificationCard, MagnifyingGlass, Plus, Trash, Eye, UploadSimple, FilePdf, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const IdDocumentsPanel = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const canWrite = (user?.permissions || []).includes("manage_accounts");
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ person_name: "", doc_type: "id", number: "", label: "", note: "", user_id: "none", expires_at: "" });
  const [file, setFile] = useState(null);
  const [expToggle, setExpToggle] = useState(false);

  const daysTo = (d) => (d ? Math.ceil((new Date(d + "T00:00:00") - new Date()) / 86400000) : null);
  const expStatus = (doc) => { const n = daysTo(doc.expires_at); if (n === null) return ""; if (n < 0) return "expired"; if (n <= 30) return "soon"; return ""; };

  const load = (query = "") => {
    api.get(`/admin/id-documents${query ? `?q=${encodeURIComponent(query)}` : ""}`).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  useEffect(() => { const id = setTimeout(() => load(q), 350); return () => clearTimeout(id); }, [q]); // eslint-disable-line

  const submit = async () => {
    if (!form.person_name.trim()) { toast.error(t("doc_person_required")); return; }
    if (!file) { toast.error(t("doc_file_required")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("person_name", form.person_name);
      fd.append("doc_type", form.doc_type);
      fd.append("number", form.number);
      fd.append("label", form.label);
      fd.append("note", form.note);
      if (form.user_id && form.user_id !== "none") fd.append("user_id", form.user_id);
      if (form.expires_at) fd.append("expires_at", form.expires_at);
      fd.append("file", file);
      await api.post("/admin/id-documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("saved"));
      setOpen(false);
      setForm({ person_name: "", doc_type: "id", number: "", label: "", note: "", user_id: "none", expires_at: "" });
      setFile(null);
      load(q);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("doc_confirm_delete"))) return;
    try { await api.delete(`/admin/id-documents/${id}`); load(q); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/admin/id-documents/${fmt}${q ? `?q=${encodeURIComponent(q)}` : ""}`, { responseType: "blob" });
      const type = fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url; a.download = `documentos.${fmt === "export" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  const typeLabel = (tp) => ({ id: t("doc_type_id"), passport: t("doc_type_passport"), license: t("doc_type_license"), other: t("doc_type_other") }[tp] || tp);
  const isPdf = (url) => (url || "").toLowerCase().endsWith(".pdf");

  return (
    <div data-testid="id-documents-panel">
      <div className="flex items-center gap-2 mb-6">
        <IdentificationCard size={26} weight="duotone" className="text-emerald-700" />
        <div>
          <h2 className="font-display text-2xl text-emerald-900">{t("documents_title")}</h2>
          <p className="text-sm text-slate-500">{t("documents_desc")}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="doc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("doc_search_ph")} className="h-11 pl-10" />
        </div>
        <button type="button" onClick={() => download("pdf")} data-testid="doc-export-pdf" className="inline-flex items-center gap-1 text-sm font-semibold text-red-700 bg-red-50 hover:bg-red-100 px-3 h-11 rounded-lg"><FilePdf size={16} weight="bold" /> {t("doc_export_pdf")}</button>
        <button type="button" onClick={() => download("export")} data-testid="doc-export-excel" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 h-11 rounded-lg"><FileXls size={16} weight="bold" /> {t("doc_export_excel")}</button>
        <button type="button" onClick={() => setExpToggle((v) => !v)} data-testid="doc-expiring-toggle" className={`inline-flex items-center gap-1 text-sm font-semibold px-3 h-11 rounded-lg ${expToggle ? "bg-amber-500 text-white" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}>{t("doc_expiring_toggle")}</button>
        {canWrite && (
          <Button data-testid="doc-add-btn" onClick={() => setOpen(true)} className="bg-emerald-800 hover:bg-emerald-900 h-11">
            <Plus size={16} weight="bold" className="mr-1" /> {t("doc_add")}
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center text-slate-400" data-testid="doc-empty">{t("doc_no_results")}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(expToggle ? items.filter((d) => expStatus(d)) : items).map((d) => {
            const es = expStatus(d);
            return (
            <div key={d.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`doc-card-${d.id}`}>
              <a href={`${BACKEND}${d.image_url}`} target="_blank" rel="noreferrer" className="block bg-slate-50 h-40 flex items-center justify-center overflow-hidden">
                {isPdf(d.image_url) ? (
                  <span className="text-slate-400 flex flex-col items-center gap-1"><Eye size={26} /> PDF</span>
                ) : (
                  <img src={`${BACKEND}${d.image_url}`} alt={d.person_name} className="w-full h-full object-cover" />
                )}
              </a>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-emerald-900 truncate">{d.person_name}</p>
                    <p className="text-xs text-slate-500">{typeLabel(d.doc_type)}{d.number ? ` · ${d.number}` : ""}</p>
                  </div>
                  <span className="text-[10px] font-semibold uppercase bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full flex-shrink-0">{typeLabel(d.doc_type)}</span>
                </div>
                {d.client_name ? <p className="text-xs text-purple-700 mt-1 truncate" data-testid={`doc-client-${d.id}`}>🔗 {t("doc_linked_to")}: {d.client_name}</p> : null}
                {d.expires_at ? <p className={`text-xs mt-1 font-semibold ${es === "expired" ? "text-red-600" : es === "soon" ? "text-amber-600" : "text-slate-500"}`} data-testid={`doc-expiry-${d.id}`}>{es === "expired" ? t("doc_expired") : es === "soon" ? t("doc_expiring_soon") : t("doc_expires")}: {d.expires_at}</p> : null}
                {d.label ? <p className="text-xs text-slate-600 mt-1 truncate">{d.label}</p> : null}
                {d.note ? <p className="text-xs text-slate-400 mt-1 line-clamp-2">{d.note}</p> : null}
                <div className="flex items-center justify-between mt-3">
                  <a href={`${BACKEND}${d.image_url}`} target="_blank" rel="noreferrer" data-testid={`doc-view-${d.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900"><Eye size={14} weight="bold" /> {t("doc_view")}</a>
                  {canWrite && (
                    <button type="button" onClick={() => remove(d.id)} data-testid={`doc-del-${d.id}`} className="text-slate-400 hover:text-red-600" title={t("delete")} aria-label={t("delete")}><Trash size={15} weight="bold" /></button>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="doc-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("doc_add")}</DialogTitle>
            <DialogDescription>{t("documents_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-emerald-900">{t("doc_person")}</Label>
              <Input data-testid="doc-person" value={form.person_name} onChange={(e) => setForm({ ...form, person_name: e.target.value })} className="h-11 mt-1.5" />
            </div>
            <div>
              <Label className="text-emerald-900">{t("doc_client")}</Label>
              <Select value={form.user_id} onValueChange={(v) => {
                const c = clients.find((x) => x.user.id === v);
                setForm({ ...form, user_id: v, person_name: form.person_name || (c ? c.user.name : "") });
              }}>
                <SelectTrigger className="h-11 mt-1.5" data-testid="doc-client"><SelectValue placeholder={t("doc_no_client")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("doc_no_client")}</SelectItem>
                  {clients.map((c) => <SelectItem key={c.user.id} value={c.user.id}>{c.user.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-emerald-900">{t("doc_type")}</Label>
                <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                  <SelectTrigger className="h-11 mt-1.5" data-testid="doc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="id">{t("doc_type_id")}</SelectItem>
                    <SelectItem value="passport">{t("doc_type_passport")}</SelectItem>
                    <SelectItem value="license">{t("doc_type_license")}</SelectItem>
                    <SelectItem value="other">{t("doc_type_other")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-emerald-900">{t("doc_number")}</Label>
                <Input data-testid="doc-number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="h-11 mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="text-emerald-900">{t("doc_label")}</Label>
              <Input data-testid="doc-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="h-11 mt-1.5" />
            </div>
            <div>
              <Label className="text-emerald-900">{t("doc_expiry")}</Label>
              <Input type="date" data-testid="doc-expiry" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="h-11 mt-1.5" />
            </div>
            <div>
              <Label className="text-emerald-900">{t("doc_note")}</Label>
              <Textarea data-testid="doc-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" rows={2} />
            </div>
            <div>
              <Label className="text-emerald-900">{t("doc_file")}</Label>
              <label className="mt-1.5 flex items-center gap-2 h-11 px-3 rounded-lg border border-dashed border-emerald-300 text-sm text-emerald-700 cursor-pointer hover:bg-emerald-50">
                <UploadSimple size={18} weight="bold" />
                <span className="truncate">{file ? file.name : t("doc_file_hint")}</span>
                <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="doc-file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <Button data-testid="doc-submit" onClick={submit} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 w-full">{t("doc_add")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
